/**
 * Function to decode extrapolate the payload from a valid jwt
 * @param {string} jwt The jwt to decode
 * @returns An object with the jwt payload or null
 */
const decodeJwt = (jwt) => {
  try {
    const [, payload] = jwt.split(".");
    if (!payload) return null;
    // JWT uses base64url, normalize to base64 and pad
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, "=");
    if (typeof atob === "function") {
      return JSON.parse(atob(padded));
    }
    if (typeof Buffer !== "undefined") {
      return JSON.parse(Buffer.from(padded, "base64").toString("utf8"));
    }
    return null;
  } catch (e) {
    return null;
  }
};

const DEMO_PATIENT_DEV = "2852bd23-d877-4682-8784-b4848ff09d68";
const DEMO_PATIENT_PROD = "3d12d014-966b-4d8d-997a-a2a156b1397c";

/**
 * Formatter for dates
 */
const dateFormatter = new Intl.DateTimeFormat("it", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Function to format a date
 * @param {Date | string} date the date to format either as a valid date or as a string
 * @returns A string readable representation of the date or null if it's an invalid input
 */
const formatDate = (date) => {
  if (!(date instanceof Date || typeof date === "string")) return null;
  const toFormat = new Date(date);
  if (isNaN(toFormat)) return null;
  return dateFormatter.format(toFormat);
};
const dateFormatterComplete = new Intl.DateTimeFormat("it-IT", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});
//Calculate start of session with DTO session
const getSessionStart = (session) => {
  //convert the sessionstart to date
  const therapistStartDate = new Date(`${session.sessionStart}Z`);
  //convert the patientSessionStart to date
  const patientStartDate = new Date(`${session.patientSessionStart}Z`);
  //check if both dates are valid date, this is made with the reduce function
  //the accumulator start as true and it loops through both dates
  //ANDing the value of !isNaN (which is true if the date is valid false otherwise)
  const isBothValidDates = [therapistStartDate, patientStartDate].reduce(
    (accumulator, current) => accumulator && !isNaN(current),
    true
  );
  //if both are valid dates return the highest of the two otherwise return null because is not started yet
  //(because one of the two dates is not yet set)
  return isBothValidDates
    ? new Date(Math.max(therapistStartDate, patientStartDate))
    : null;
};
const getSessionEnd = (session) => {
  //convert the sessionend to date
  const therapistEndDate = new Date(`${session.sessionEnd}Z`);
  //convert the patientSessionStart to date
  const patientEndDate = new Date(`${session.patientSessionEnd}Z`);
  //check if both dates are valid date, this is made with the reduce function
  //the accumulator start as true and it loops through both dates
  //ANDing the value of !isNaN (which is true if the date is valid false otherwise)
  const isBothValidDates = [therapistEndDate, patientEndDate].reduce(
    (accumulator, current) => accumulator && !isNaN(current),
    true
  );
  //check if both dates are invalid date, this is made with the reduce function
  //the accumulator start as true and it loops through both dates
  //ANDing the value of isNaN (which is true if the date is valid false otherwise)
  //note that this is different from !isBothValidDates because it's not possible
  //to distinguish between a single or a double date being invalid
  const isBothInvalidDates = [therapistEndDate, patientEndDate].reduce(
    (accumulator, current) => accumulator && isNaN(current),
    true
  );
  //if both dates are valid return the minumun value
  if (isBothValidDates) {
    return new Date(Math.min(therapistEndDate, patientEndDate));
  }
  //if both are invalid the session does not have an end date yet
  if (isBothInvalidDates) {
    return null;
  }
  //if just one of the two dates is valid return the valid one
  //this means at leat one of the two has already closed the video call
  return !isNaN(therapistEndDate) ? therapistEndDate : patientEndDate;
};

//date formatter to format a date into string
const onlyDateFormatter = new Intl.DateTimeFormat("it-IT", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

//date formatter to format a date into string
const hoursFormatter = new Intl.DateTimeFormat("it-IT", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/**
 * Get the current week initial and end date
 * @param {Date|string} [startingDate] - the starting date to get the week, default to Now
 * @returns {[Date, Date]}
 */
const getCurrentWeek = (startingDate) => {
  const today = startingDate ? new Date(startingDate) : new Date();
  today.setHours(0, 0, 0, 0);
  let day = today.getDay() - 1;
  if (day < 0) {
    day = 6;
  }
  const time = today.getTime();
  const firstDay = new Date(time - day * 3600 * 24 * 1000);
  const lastDay = new Date(time + (6 - day) * 3600 * 24 * 1000);
  return [firstDay, lastDay];
};

//method to start the download of a file upon receiving a RawDocumentDTO
let downloadFile = (rawFile) => {
  //converting the base64 string to a byt array
  const bytes = Uint8Array.from(atob(rawFile.base64File), (c) =>
    c.charCodeAt(0)
  );
  //creating the new blob
  let blob = new Blob([bytes]);
  //creating a ficticious a element that will never be added to the page
  let link = document.createElement("a");
  //setting the blob url as the href
  link.href = window.URL.createObjectURL(blob);
  //setting the download name
  link.download = rawFile.fileName;
  //programmatically clicking on it to start the download
  link.click();
};

export {
  decodeJwt,
  dateFormatter,
  formatDate,
  getSessionStart,
  getSessionEnd,
  dateFormatterComplete,
  onlyDateFormatter,
  hoursFormatter,
  getCurrentWeek,
  downloadFile,
  DEMO_PATIENT_DEV,
  DEMO_PATIENT_PROD,
};
